/*
 * Copyright (c) 2023-2024. Haodong JU
 */

import { groupBy, sumBy } from 'lodash'
import dayjs from 'dayjs'
import hillo from 'hillo'
import GlobalSettings, { GlobalConfigLang } from '@/model/global/GlobalSettings'
import { getActiveProductCode } from '@/model/dataLayer/api/SaaS/user'
import { ProductCodes } from '@/model/dataLayer/api/SaaS/product'
import { rateDisplay } from '@/model/utils/utils'
import i18n from '@/i18n'
import { setHouseRatesColumn } from '@/model/dataLayer/api/report/report'
import { collection, doc, getDoc, query } from 'firebase/firestore'
import { FireBaseStore } from '@/model/firebase/google-fire-base'
import { resultOfId } from '@/model/firebase/queryUtils'
import { getBaseAndUrlForDeviceId } from '@/model/dataLayer/service/RestaurantInfoService'
import { storageNewApiHost } from '@/model/dataLayer/api/storage/inventoryUtils'

export async function getAllStoreInfo (deviceIds, date) {
  const results = []
  for (const deviceId of deviceIds) {
    try {
      const result = {}
      const { url } = await getBaseAndUrlForDeviceId(deviceId)
      const dashboardInfo = await loadDashboardDataByDeviceId(date, url)
      let analysisInfo = []
      analysisInfo = await getBusinessReportInfoByDeviceId(date, url)
      // if (!requireOfflineMode()) {
      //   const totalCostInfo = await getTotalCostInfoByDeviceId(date, deviceId)
      //   analysisInfo.dailyBusinessInfo.forEach(it => {
      //     const res = totalCostInfo.find(x => x.date === it.date)
      //     it.orderInfo.dailyCost = res?.dailyTotal ?? 0
      //     it.orderInfo.dailyProfit = it.orderInfo.totalTotalPrice -
      //       (res?.dailyTotal ?? 0) - it.orderInfo.totalTax
      //   })
      // }
      const dishList = dashboardInfo?.dishStatistic?.filter(
        it => it.categoryTypeId !== '9') ?? []
      const drinkList = dashboardInfo?.dishStatistic?.filter(
        it => it.categoryTypeId === '9') ?? []
      result.tableTotalCount = parseFloat(analysisInfo?.totalTableCount)
      result.totalFinishOrder = dashboardInfo?.taxInfos?.content?.total ?? 0
      result.totalUnpaid = parseFloat(dashboardInfo?.taxInfos?.content?.unpaidTotal ?? 0)
      result.dishTotal = sumBy(dishList, (it) => {
        return it.price * it.totalCount
      }) ?? 0
      result.drinkTotal = sumBy(drinkList, (it) => {
        return it.price * it.totalCount
      }) ?? 0
      result.newProfit = sumBy(analysisInfo?.dailyBusinessInfo, (x) => {
        return x.orderInfo.dailyProfit
      })
      result.togoTotal = parseFloat(
        dashboardInfo?.salesInfo?.consumeTypeInfo?.find(a => a.consumeTypeId === '2')?.total ?? 0)
      result.orderCount = parseInt(dashboardInfo?.orderCount ?? 0)
      result.guestCount = parseInt(dashboardInfo?.getGuestTotalCount ?? 0)
      result.avgGuestPrice = parseFloat(dashboardInfo?.guestAvgPrice ?? 0)
      result.discountList = dashboardInfo?.taxInfos?.content?.discount?.map(b => b.orderInfo)?.flat() ?? []
      result.discountList.forEach(it => {
        it.id = deviceId + '|' + it.id
      })
      result.stornoList = dashboardInfo?.taxInfos?.content?.storno ?? []
      result.stornoList.forEach(it => {
        it.id = deviceId + '|' + it.orderId
      })
      result.stornoTotal = (dashboardInfo?.taxInfos?.content?.storno ?? []).reduce(
        (sum, it) => sum + Math.abs(parseFloat(it?.price)), 0)
      result.discountTotal = (dashboardInfo?.taxInfos?.content?.discount ?? []).reduce(
        (sum, it) => sum + parseFloat(it.orderInfo?.value), 0)
      result.inHouseOrderCount = parseInt(dashboardInfo?.inHouseOrderCount ?? 0)
      result.avgTableTurnOverRate = sumBy(analysisInfo.dailyBusinessInfo, (x) => {
        return x.orderInfo.dailyTurnoverRate
      }) / analysisInfo.dailyBusinessInfo.length || 0
      result.smallSelector = dashboardInfo?.spanTurnover.smallSelector ?? 'hour'
      result.spanTurnover = dashboardInfo?.spanTurnover?.result ?? []
      result.spanTurnover.forEach(it => {
        it.deviceId = deviceId
      })
      const billContent = dashboardInfo?.taxInfos?.content
      result.paidInfo = dashboardInfo?.taxInfos?.content.paidInfo
      result.taxGroupInfo = billContent?.taxInfos.map(it => {
        it.taxType = rateDisplay(it.taxRatePercentage / 100)
        return it
      })?.filter(t => t.consumeTypeName === 'Total')
      result.total = billContent.total
      result.totalTax = billContent.totalTax
      result.totalTe = billContent.totalTe
      result.collectSalesDate = dashboardInfo?.salesInfo?.consumeTypeInfo ?? []
      result.url = url
      results.push(result)
    } catch (e) {
      console.log(e)
      console.log(deviceId, '更新后端版本看看！')
    }
  }
  if (results.length > 0) {
    const standard = results[0]
    return Object.fromEntries(Object.keys(standard).map(key => {
      if (key === 'url') {
        return [key, results.map(it => it[key])]
      }
      let t = null
      switch (typeof standard[key]) {
        case 'number':
          t = sumBy(results, key)
          break
        case 'string':
          t = standard[key]
          break
        case 'object':
          t = results.map(it => ({
            deviceId: it.deviceId,
            result: it[key]
          }))
      }
      return [key, t]
    }))
  } else {
    return null
  }
}

export function getFlattenedArray (dashboardInfo, key) {
  return dashboardInfo?.[key]?.map(it => it?.result ?? [])?.flat() ?? []
}

export function changeSymbol (item) {
  return item.replace('.', '').replace(',', '.')
}

export async function getBusinessReportInfoByDeviceId (time, deviceIdUrl) {
  let fromTime = time[0]
  let toTime = time[1]
  if (fromTime === toTime) {
    fromTime = dayjs(fromTime).add(4, 'hour').format('YYYY-MM-DD HH:mm:ss')
    toTime = dayjs(toTime).add(1, 'day').add(4, 'hour').subtract(1, 'second').format('YYYY-MM-DD HH:mm:ss')
  } else {
    fromTime = dayjs(fromTime).add(4, 'hour').format('YYYY-MM-DD HH:mm:ss')
    toTime = dayjs(toTime).add(1, 'day').add(4, 'hour').subtract(1, 'second').format('YYYY-MM-DD HH:mm:ss')
  }
  const res = (await hillo.post(deviceIdUrl + '/PHP/' + 'BackendData.php?op=getBusinessReport', {
    fromDateTime: fromTime,
    toDateTime: toTime,
    lang: GlobalSettings.locale
  })).content
  res.dailyBusinessInfo
    .forEach(it => {
      it.orderInfo.dailyTurnoverRate =
        (it.orderInfo.inHouseOrderCount - res.totalTableCount) /
        res.totalTableCount > 0
          ? (it.orderInfo.inHouseOrderCount - res.totalTableCount) /
          res.totalTableCount
          : 0
    })
  res.avgTurnoverRate = res.dailyBusinessInfo
    .reduce((acc, curr) => acc + curr.orderInfo.dailyTurnoverRate, 0) /
    res.dailyBusinessInfo.length || 0
  return res
}

export async function loadDashboardDataByDeviceId (dateRange, deviceIdUrl) {
  const [startDate, endDate] = dateRange
  return await hillo.get(deviceIdUrl + '/PHP/' + 'DashboardData.php', {
    startDate,
    endDate,
    lang: GlobalConfigLang.lang
  }, { timeout: 1000 * 60 * 2 })
}

export async function getTotalCostInfoByDeviceId (timespan, deviceId) {
  const activeProductCodeList = await getActiveProductCode(deviceId)
  return activeProductCodeList.includes(ProductCodes.inventory)
    ? await getAllCostWithStorageByDeviceId(timespan, deviceId)
    : await getAllCostWithOutStorageByDeviceId(timespan, deviceId)
}

export async function getAllDeviceIdPaymentList (deviceIdUrl) {
  return (await hillo.get(deviceIdUrl + '/PHP/' + 'PayMethod.php', {
    showHidden: 1
  })).content
}

export async function getAllDeviceIdCashBookList (deviceIdUrl, timespan) {
  const [fromDate, toDate] = timespan
  return (await hillo.get(deviceIdUrl + '/PHP/' + 'Complex.php?op=getCashInOutDetail', {
    fromDate,
    toDate
  })).content.map(it => {
    if (it.cashAccountNote.toLowerCase() === 'umsatz' || it.cashAccountNote.toLowerCase() === 'trinkgeld') {
      it.cashInCome = 1
    } else {
      it.cashInCome = 2
    }
    if (!it.name) {
      if (it.payLogAmount >= 0) {
        it.name = i18n.t('Revenue')
      } else {
        it.name = i18n.t('Expenditure')
      }
    }
    return it
  })
}

export async function getAllCostWithStorageByDeviceId (timespan, deviceId) {
  const storageFromTime = timespan[0] + 'T04:00:00'
  const storageToTime = dayjs(timespan[1]).add(1, 'day').format('YYYY-MM-DD') + 'T03:59:59'
  const materialOutList = (await getInOutLogByDeviceId(storageFromTime, storageToTime, deviceId)).map(it => {
    it.changeLog.createdAt = dayjs(it.changeLog.createdAt).format('YYYY-MM-DD')
    return it.changeLog
  }).filter(x => x.operation === 'Out') ?? []
  const listByDate = groupBy(materialOutList, 'createdAt')
  const realCost = Object.fromEntries(Object.entries(listByDate).map(it => {
    it[1] = Math.abs(sumBy(it[1], 'costLiteral'))
    return it
  }))
  const historyReportList = await getReportDateByDeviceId(deviceId)
  const defaultFixedList = [
    (await getFixedDefaultCostListByDeviceId(deviceId)),
    await getFixedExtraCostListByDeviceId(deviceId)
  ].flat()
  const defaultFixedCost = sumBy(defaultFixedList, function (o) {
    return parseFloat(o.value)
  })
  const defaultPersonCost = sumBy(defaultFixedList.slice(0, 2), function (o) {
    return parseFloat(o.value)
  })
  const defaultHouseCost = defaultFixedList[2]?.value ?? 0
  const defaultHydropowerCost = defaultFixedList[3]?.value ?? 0
  const realSelectedHistoryReportCost = []
  const res = setHouseRatesColumn(timespan)
  for (const item of res) {
    const currentMonth = dayjs(item).format('YYYY-MM')
    const thisMonth = dayjs().format('YYYY-MM')
    if (historyReportList.includes(currentMonth) && currentMonth !== thisMonth) {
      const fixedList = await getFixedCostByDeviceId(currentMonth, deviceId)
      const fixedCost = sumBy(fixedList, function (o) {
        return parseFloat(o.value)
      })
      const personCost = sumBy(fixedList.slice(0, 2), function (o) {
        return parseFloat(o.value)
      })
      const houseCost = fixedList[2]?.value ?? 0
      const hydropowerCost = fixedList[3]?.value ?? 0
      const dayLength = dayjs(currentMonth).daysInMonth()
      realSelectedHistoryReportCost.push({
        date: item,
        dailyPurchasing: realCost[item] ?? 0,
        dailyFixed: fixedCost / dayLength,
        dailyTotal: ((realCost[item] ?? 0) + (fixedCost / dayLength)).toFixed(2),
        dailyPerson: (personCost / dayLength).toFixed(2),
        dailyHouse: (houseCost / dayLength).toFixed(2),
        dailyHydropower: (hydropowerCost / dayLength).toFixed(2)
      })
    } else {
      const dayLength = dayjs(item).daysInMonth()
      realSelectedHistoryReportCost.push({
        date: item,
        dailyPurchasing: realCost[item] ?? 0,
        dailyFixed: defaultFixedCost / dayLength,
        dailyTotal: ((realCost[item] ?? 0) + (defaultFixedCost / dayLength)).toFixed(2),
        dailyPerson: (defaultPersonCost / dayLength).toFixed(2),
        dailyHouse: (defaultHouseCost / dayLength).toFixed(2),
        dailyHydropower: (defaultHydropowerCost / dayLength).toFixed(2)
      })
    }
  }
  return realSelectedHistoryReportCost
}

export async function getInOutLogByDeviceId (dateTimeFrom = null, dateTimeTo = null, deviceId) {
  return (await hillo.jsonPost(storageNewApiHost + 'resourceLog/' + 'getDetailList', {
    dateTimeFrom,
    dateTimeTo
  }, await getRealADIByDeviceId(deviceId))).data
}

export async function getRealADIByDeviceId (deviceId) {
  return { params: { ADI: deviceId } }
}

export async function getReportDateByDeviceId (deviceId) {
  const ref = query(collection(FireBaseStore, 'storeCost', deviceId, 'storeFixedCost'))
  return (await resultOfId(ref)).map(it => it.id)
}

export async function getFixedDefaultCostListByDeviceId (deviceId) {
  const defaultRef = doc(FireBaseStore, 'storeCost', deviceId, 'storeFixedDefaultList', 'list')
  return (await getDoc(defaultRef)).data()?.list ?? []
}

export async function getFixedExtraCostListByDeviceId (deviceId) {
  const defaultRef = doc(FireBaseStore, 'storeCost', deviceId, 'storeFixedExtraList', 'list')
  return (await getDoc(defaultRef)).data()?.list ?? []
}

export async function getFixedCostByDeviceId (date, deviceId) {
  const fixedRef = doc(FireBaseStore, 'storeCost', deviceId, 'storeFixedCost', date)
  return (await getDoc(fixedRef)).data()?.fixedList ?? []
}

export async function getAllCostWithOutStorageByDeviceId (timespan, deviceId) {
  const res = setHouseRatesColumn(timespan)
  const defaultPurchasingList = (await getPurchasingDefaultCostListByDeviceId(deviceId))
  const defaultFixedList = (await getFixedDefaultCostListByDeviceId(deviceId))
  const defaultFixedCost = sumBy(defaultFixedList, function (o) {
    return parseFloat(o.value)
  })
  const defaultPersonCost = sumBy(defaultFixedList.slice(0, 2), function (o) {
    return parseFloat(o.value)
  })
  const defaultHouseCost = defaultFixedList[2]?.value ?? 0
  const defaultHydropowerCost = defaultFixedList[3]?.value ?? 0
  const defaultPurchasingCost = sumBy(defaultPurchasingList, function (o) {
    return parseFloat(o.value)
  })
  const defaultTotal = defaultFixedCost + defaultPurchasingCost
  const historyReportList = await getReportDateByDeviceId(deviceId)
  const realSelectedHistoryReportCost = []

  for (const item of res) {
    const currentMonth = dayjs(item).format('YYYY-MM')
    if (historyReportList.includes(currentMonth)) {
      const fixedList = await getFixedCostByDeviceId(currentMonth, deviceId)
      const fixedCost = sumBy(fixedList, function (o) {
        return parseFloat(o.value)
      })
      const personCost = sumBy(fixedList.slice(0, 2), function (o) {
        return parseFloat(o.value)
      })
      const houseCost = fixedList[2]?.value ?? 0
      const hydropowerCost = fixedList[3]?.value ?? 0
      const purchasingCost = sumBy(await getPurchasingCostByDeviceId(currentMonth, deviceId), function (o) {
        return parseFloat(o.value)
      })
      const dayLength = dayjs(currentMonth).daysInMonth()
      realSelectedHistoryReportCost.push({
        date: item,
        dailyPurchasing: (purchasingCost / dayLength).toFixed(2),
        dailyFixed: (fixedCost / dayLength).toFixed(2),
        dailyTotal: ((purchasingCost + fixedCost) / dayLength).toFixed(2),
        dailyPerson: (personCost / dayLength).toFixed(2),
        dailyHouse: (houseCost / dayLength).toFixed(2),
        dailyHydropower: (hydropowerCost / dayLength).toFixed(2)
      })
    } else {
      const dayLength = dayjs(currentMonth).daysInMonth()
      realSelectedHistoryReportCost.push({
        date: item,
        dailyPurchasing: (defaultPurchasingCost / dayLength).toFixed(2),
        dailyFixed: (defaultFixedCost / dayLength).toFixed(2),
        dailyTotal: (defaultTotal / dayLength).toFixed(2),
        dailyPerson: (defaultPersonCost / dayLength).toFixed(2),
        dailyHouse: (defaultHouseCost / dayLength).toFixed(2),
        dailyHydropower: (defaultHydropowerCost / dayLength).toFixed(2)
      })
    }
  }
  return realSelectedHistoryReportCost
}

export async function getPurchasingDefaultCostListByDeviceId (deviceId) {
  const defaultRef = doc(FireBaseStore, 'storeCost', deviceId, 'storePurchasingDefaultList', 'list')
  return (await getDoc(defaultRef)).data()?.list ?? []
}

export async function getPurchasingCostByDeviceId (date, deviceId) {
  const purchaseRef = doc(FireBaseStore, 'storeCost', deviceId, 'storePurchasingCost', date)
  return (await getDoc(purchaseRef)).data()?.purchaseList ?? []
}
